import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado

const TopicosPage = ({ materiaId, materiaNome, onVoltar, onSubtopicoClick }) => {
  const [topicos, setTopicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    const fetchTopicos = async () => {
      try {
        // Esta rota requer autenticação, então verificamos o token
        const userInfo = JSON.parse(localStorage.getItem('userInfo'));
        if (!userInfo || !userInfo.token) {
          setError('Usuário não autenticado. Por favor, faça login.');
          setLoading(false);
          return;
        }

        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`
          }
        };

        const response = await api.get(`/api/materias/${materiaId}/topicos`, config);
        // Correção: o backend retorna um array diretamente, não um objeto com propriedade 'topicos'
        const fetchedTopicos = Array.isArray(response.data) ? response.data : [];
        setTopicos(fetchedTopicos);
      } catch (err) {
        console.error('Erro ao buscar tópicos:', err);
        setError('Falha ao carregar os tópicos. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    if (materiaId) {
      fetchTopicos();
    }
  }, [materiaId]);

  // Função para gerar sugestões com base no termo de busca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const allTopicNames = [];

    // Coletar todos os nomes de tópicos e subtópicos
    topicos.forEach(topico => {
      allTopicNames.push({ name: topico.nome, type: 'tópico', id: topico._id });

      if (topico.subtopicos && Array.isArray(topico.subtopicos)) {
        topico.subtopicos.forEach(subtopico => {
          allTopicNames.push({ name: subtopico.nome, type: 'subtópico', id: subtopico._id, parentId: topico._id });
        });
      }
    });

    // Filtrar sugestões com base no termo de busca
    const filteredSuggestions = allTopicNames.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSuggestions(filteredSuggestions);
    setShowSuggestions(filteredSuggestions.length > 0);
  }, [searchTerm, topicos]);

  // Função para lidar com a seleção de uma sugestão
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
    // Aqui você pode adicionar lógica adicional para focar no item selecionado
  };

  // Fechar sugestões quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando tópicos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center max-w-2xl mx-auto p-4">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        <header className="flex items-center mb-10">
          <button onClick={onVoltar} className="flex items-center text-violet-600 font-semibold hover:text-violet-800 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Voltar
          </button>
        </header>

        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">{materiaNome}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore os tópicos desta matéria e aprofunde seu conhecimento
          </p>
        </div>

        {/* Barra de pesquisa com sugestões */}
        <div className="mb-12 relative search-container">
          <div className="w-full max-w-4xl mx-auto px-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Pesquisar tópicos e subtópicos..."
                className="w-full px-6 py-4 text-lg border border-gray-300 rounded-2xl shadow-sm focus:outline-none focus:ring-4 focus:ring-violet-300 focus:border-violet-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => searchTerm && setShowSuggestions(true)}
              />
              <svg
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>

          {/* Sugestões de busca */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="w-full max-w-4xl mx-auto px-4 mt-2">
              <div className="absolute z-10 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-80 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.id}-${index}`}
                    className="px-6 py-3 hover:bg-violet-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <svg
                      className="w-5 h-5 text-violet-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <div>
                      <div className="font-medium text-gray-800">{suggestion.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{suggestion.type}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-8">
          {topicos
            .filter(topico => {
              // Se não há termo de busca, mostrar todos
              if (!searchTerm.trim()) return true;

              // Verificar se o nome do tópico corresponde
              const topicoMatches = topico.nome.toLowerCase().includes(searchTerm.toLowerCase());

              // Verificar se algum subtópico corresponde
              const subtopicosMatch = topico.subtopicos && Array.isArray(topico.subtopicos) &&
                topico.subtopicos.some(subtopico =>
                  subtopico.nome.toLowerCase().includes(searchTerm.toLowerCase())
                );

              return topicoMatches || subtopicosMatch;
            })
            .map((topico) => {
              // Filtrar subtópicos se houver busca
              const filteredSubtopicos = searchTerm.trim() && topico.subtopicos && Array.isArray(topico.subtopicos)
                ? topico.subtopicos.filter(subtopico =>
                    subtopico.nome.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                : (topico.subtopicos || []);

              return (
                <div key={topico._id} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-3xl font-bold text-gray-800">{topico.nome}</h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredSubtopicos.map((subtopico) => (
                        <div
                          key={subtopico._id}
                          onClick={() => onSubtopicoClick(subtopico)}
                          className="bg-gradient-to-br from-violet-50 to-purple-50 p-6 rounded-xl hover:from-violet-100 hover:to-purple-100 transition-all duration-300 cursor-pointer border border-violet-200 hover:border-violet-300 group"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800 group-hover:text-violet-600 transition-colors">
                              {subtopico.nome}
                            </h3>
                            <div className="bg-violet-100 rounded-full p-2">
                              <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                              </svg>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                              </svg>
                              <span>
                                {subtopico.videoaulas ? `${subtopico.videoaulas.length} videoaulas` : 'Nenhuma videoaula'}
                              </span>
                            </div>
                            {subtopico.questoes && subtopico.questoes.length > 0 && (
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <span>{subtopico.questoes.length} questões</span>
                              </div>
                            )}
                          </div>
                          <div className="mt-4 pt-4 border-t border-violet-200">
                            <span className="text-violet-600 font-medium text-sm group-hover:underline">
                              Clique para explorar →
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>

        {/* Anúncio seguro após o conteúdo principal */}
      </div>
    </div>
  );
};

export default TopicosPage;
